import { type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import AbstractSlackService from './AbstractSlackService';
import { getSlackServiceAnalytics } from './analytics';
import {
	DEFAULT_STARGATE_SERVICE_URL,
	EXUS_THIRDPARTY_PATH,
	ITPT_PATH,
	ITPT_SLACK_CHANNELS,
} from './constants';
import {
	type FetchSlackUsersResult,
	type GetSlackChannelsOrGroupResult,
	type PlatformSlackUsersResponse,
	type SlackChannelsOrGroupsResponse,
} from './share-to-slack/common/types';
import {
	abortUfoExperience,
	failUfoExperience,
	startUfoExperience,
	succeedUfoExperience,
	UfoExperiences,
} from './ufo-experiences';
import { httpRetryPolicy, retryFetch } from './util';

const USER_FETCH_LIMIT = 500;

const RETRY_OPTIONS = {
	maxAttempts: 3,
	backoffTimeMs: 200,
	rateLimitBackoffTimeMs: 1000,
};

export default class PlatformSlackService extends AbstractSlackService {
	constructor(cloudId: string, createUIAnalyticsEvent: CreateUIAnalyticsEvent) {
		const analytics = getSlackServiceAnalytics(createUIAnalyticsEvent);
		super(cloudId, 'platform', analytics);
	}

	streamUsers(
		teamId: string,
		onResponse: (result: FetchSlackUsersResult) => void,
		cursor?: string,
	) {
		startUfoExperience(UfoExperiences.PLATFORM_SLACK_USERS_PAGE_FETCHED, {
			limit: USER_FETCH_LIMIT,
		});

		if (!cursor) {
			this.userStream = { count: 0, pages: 0, finishedLoading: false };
		}
		const queryString = new URLSearchParams({
			teamId,
			cloudId: this.cloudId,
			limit: USER_FETCH_LIMIT.toString(),
			...(cursor ? { cursor } : {}),
		});

		retryFetch<PlatformSlackUsersResponse>({
			request: () =>
				fetch(`${DEFAULT_STARGATE_SERVICE_URL}${EXUS_THIRDPARTY_PATH}/slack/users?${queryString}`, {
					signal: this.abortController?.signal,
				}),
			canRetryCheck: httpRetryPolicy(RETRY_OPTIONS),
		}).then((resp) => {
			if (!resp.ok) {
				if (!resp.aborted) {
					this.analytics.onFetchUsersFailed({
						teamId,
						page: this.userStream.pages,
						status: resp.status,
						errorMessage: resp.message,
					});

					failUfoExperience(UfoExperiences.PLATFORM_SLACK_USERS_PAGE_FETCHED);
				} else {
					abortUfoExperience(UfoExperiences.PLATFORM_SLACK_USERS_PAGE_FETCHED);
				}
				onResponse(resp);

				return;
			}

			this.userStream.pages++;
			this.userStream.count += resp.result.users.length;
			this.userStream.finishedLoading = !!resp.result.nextCursor;
			onResponse({
				ok: true,
				result: resp.result.users.map(
					({ id, email, displayName, realName: name, avatar: avatarUrl }) => ({
						id,
						name,
						displayName,
						avatarUrl,
						email,
						type: 'directMessage',
					}),
				),
				nextCursor: resp.result.nextCursor,
			});

			succeedUfoExperience(UfoExperiences.PLATFORM_SLACK_USERS_PAGE_FETCHED, {
				numResults: resp.result.users.length,
			});

			if (resp.result.nextCursor) {
				this.streamUsers(teamId, onResponse, resp.result.nextCursor);
			} else {
				this.analytics.onFetchUsersSucceeded({
					teamId,
					users: this.userStream.count,
					pages: this.userStream.pages,
				});
			}
		});
	}

	/*
	 * Temporary experiment endpoint for Invite 3rd Party Team (ITPT)
	 */
	streamChannelsExperiment(
		workspaceId: string,
		onResponse: (result: GetSlackChannelsOrGroupResult) => void,
		onError: (error: Error) => void,
		cursor?: string,
	) {
		if (!cursor) {
			this.channelStream = { count: 0, pages: 0, finishedLoading: false };
		}

		const queryString = new URLSearchParams({
			workspaceId,
			...(cursor ? { cursor } : {}),
		});

		retryFetch<SlackChannelsOrGroupsResponse>({
			request: () =>
				fetch(`${DEFAULT_STARGATE_SERVICE_URL}${ITPT_PATH}${ITPT_SLACK_CHANNELS}?${queryString}`, {
					signal: this.abortController?.signal,
				}),
			canRetryCheck: httpRetryPolicy(RETRY_OPTIONS),
		})
			.then((resp) => {
				if (!resp.ok) {
					onResponse(resp);
					return;
				}
				this.channelStream.pages++;
				this.channelStream.count += resp.result.channels.length;
				this.channelStream.finishedLoading = !!resp.result.nextCursor;
				onResponse({
					ok: true,
					result: resp.result.channels,
					nextCursor: resp.result.nextCursor,
				});

				if (resp.result.nextCursor) {
					this.streamChannelsExperiment(workspaceId, onResponse, onError, resp.result.nextCursor);
				}
			})
			.catch((error) => {
				onError(error);
			});
	}

	// This was necessary since it was abstract in the base class
	// but is not used in this slack service
	async primeConsentUrl() {}
}
